import { IFluidObject } from '@fluidframework/core-interfaces';
import { IContainerRuntime } from '@fluidframework/container-runtime-definitions';
import {
  IContainerServiceFactory,
  MenuViewProvider as MenuServiceType,
  ITelemetryNamespaceLogger,
  StylingService,
  MenuViewProps,
  MenuView,
  StylingProviderEvents
} from '@fluidx/office-fluid-types';
import { getMenuRenderer } from '../view';
import { validateThemingAccessibility } from './validateThemingAccessibility';

export class MenuService implements MenuServiceType {
  private initializationPromise: Promise<void>;
  private logger: ITelemetryNamespaceLogger | undefined;
  private stylingService: StylingService | undefined;
  private stylingProperties: { [propertyName: string]: string } | undefined;

  public get MenuViewProvider() {
    return this;
  }

  public static serviceFactory: IContainerServiceFactory = {
    get IContainerServiceFactory() {
      return this;
    },
    instantiateContainerService: (runtime: IContainerRuntime): IFluidObject => {
      const loggerPromise: Promise<ITelemetryNamespaceLogger | undefined> = runtime
        .request({ url: '/_services/NamespaceLogger' })
        .then((namespaceLoggerRequest) => {
          if (namespaceLoggerRequest.status === 200 && namespaceLoggerRequest.mimeType === 'fluid/object') {
            let namespaceLogger = (namespaceLoggerRequest.value as IFluidObject).NamespaceLogger;

            return Promise.resolve(namespaceLogger);
          }
          return Promise.reject();
        })
        .catch(() => {
          // if we don't have a namespace logger, we just create the Menu View Service with an undefined logger
          return undefined;
        });

      const stylingServicePromise: Promise<StylingService | undefined> = runtime
        .request({ url: '/_services/StylingService' })
        .then((response) => {
          if (response.status === 200 && response.mimeType === 'fluid/object') {
            const componentStyleRegistry = response.value as IFluidObject;
            return componentStyleRegistry.StylingService;
          }
          return undefined;
        })
        .catch((error) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          loggerPromise.then((logger) => {
            if (logger) {
              logger.sendWithNamespace(error, 'ContainerStyleLoadError');
            }
          });
          return undefined;
        });

      return new MenuService(loggerPromise, stylingServicePromise);
    }
  };

  constructor(
    loggerPromise?: Promise<ITelemetryNamespaceLogger | undefined>,
    stylingServicePromise?: Promise<StylingService | undefined>
  ) {
    this.initializationPromise = Promise.all([
      loggerPromise?.then((logger) => (this.logger = logger)),
      stylingServicePromise?.then((stylingService) => (this.stylingService = stylingService))
    ]).then(() => {
      if (this.stylingService) {
        const readThemingProperties = (): void => {
          let cssProperties: { [propertyName: string]: string } | undefined;

          if (this.stylingService) {
            const themeProperties = Array.from(this.stylingService.themeProperties());
            themeProperties.forEach((themeProperty) => {
              if (cssProperties === undefined) {
                cssProperties = {};
              }
              cssProperties[themeProperty.name] = themeProperty.value;
            });
          }

          this.stylingProperties = cssProperties;

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          validateThemingAccessibility(this.stylingProperties, this.logger);
        };

        this.stylingService.on(StylingProviderEvents.themePropertiesChanged, () => {
          readThemingProperties();
        });
        readThemingProperties();
      }
    });
  }

  public async getView(rendererProps: MenuViewProps): Promise<MenuView | undefined> {
    await this.initializationPromise;

    return getMenuRenderer(rendererProps, () => this.stylingProperties);
  }
}

export const serviceFactory = MenuService.serviceFactory;
